import { AdditionalParticipant, Calendar, Configuration, EventDetails, GroupConfiguration, GroupEventAPIData, EnableEventTypes, ThemeConfig } from '@nylas/core';
import type { ParticipantFetch, ParticipantSearch } from '@/types';
import { debug } from '@/utils/utils';
import { ObservableMap, createStore } from '@stencil/store';
import { User } from '../common/nylas-api-request';
import { CONFIGURATION_EVENT_TYPE, EXPERIMENTAL_FEATURES } from '@/common/constants';

type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[] ? RecursivePartial<U>[] : T[P] extends object | undefined ? RecursivePartial<T[P]> : T[P];
};

export interface NylasSchedulerConfigStoreState {
  /**
   * The selected configuration.
   * This is the configuration that is currently selected, use it to populate default values for new configurations.
   */
  selectedConfiguration: RecursivePartial<Configuration> | RecursivePartial<GroupConfiguration>;
  /**
   * The list of configurations.
   * This is the list of configurations that the user has created.
   */
  configurations: Configuration[];
  /**
   * The list of calendars.
   * This is the list of calendars that the user has access to.
   */
  calendars: Calendar[];
  /**
   * The current user.
   * This is the user that is currently logged in.
   */
  currentUser: User | null;
  /**
   * The current action.
   * This is the current action that the user is performing.
   */
  action: 'create' | 'create-select' | 'edit' | null;
  /**
   * The additional participants.
   */
  additionalParticipants: AdditionalParticipant[];
  /**
   * Callback to fetch full participant info by emails when loading a config for edit.
   * Receives emails and optional grantId for scoping.
   */
  participantFetch?: ParticipantFetch;
  /**
   * Grant ID to pass to participantFetch when available (e.g. from multi-account).
   */
  participantFetchGrantId?: string;
  /**
   * Callback to search participants by query for autocomplete.
   */
  participantSearch?: ParticipantSearch;
  /**
   * The next cursor for list configurations.
   */
  listConfigurationsNextCursor: string | null;
  /**
   * The conference providers.
   */
  conferenceProviders?: Record<'microsoft' | 'zoom' | 'google', string>;
  /**
   * The flag to determine if the slug is required
   */
  requiresSlug: boolean;
  /**
   * The selected language.
   */
  selectedLanguage?: string;
  /**
   * The flag to show or hide the option to manage nylas branding in the email template.
   */
  nylasBranding?: boolean;
  /**
   * The experimental features.
   */
  experimentalFeatures?: Record<EXPERIMENTAL_FEATURES, boolean>;
  /**
   * Controls which event types are enabled in the Scheduler.
   *
   * - By default, all event types are enabled and users will see a selection screen when creating a new event.
   * - You can disable specific event types by setting them to `false`.
   * - At least one event type must remain enabled at all times.
   * - If only one event type is enabled, the event type selection screen will be skipped.
   */
  enableEventTypes?: EnableEventTypes;
  /**
   * The selected event type for creating a new configuration.
   */
  selectedEventType?: CONFIGURATION_EVENT_TYPE;
  /**
   * The group events of the selected configuration.
   */
  groupEvents: GroupEventAPIData[];
  /**
   * The selected group event being edited.
   */
  selectedGroupEvent?: GroupEventAPIData;
  /**
   * The url params with the date and time range for the current view in the scheduler editor calendar.
   */
  calendarViewDateRange?: {
    start_time: string;
    end_time: string;
  };
  /**
   * The list of master group events.
   */
  masterGroupEvents: Record<string, EventDetails>;
  /**
   * The flag to determine if the form is loading.
   */
  isLoading: boolean;
  /**
   * The theme config.
   */
  themeConfig?: ThemeConfig;
}

export type NylasSchedulerConfigStoreType = ReturnType<typeof CreateNylasSchedulerConfigStore>;

export type CreateNylasSchedulerConfigStoreReturnType = ObservableMap<NylasSchedulerConfigStoreState> & {
  /**
   * Reset the selected configuration to the default state.
   * This is used to reset the selected configuration to the default state instead of setting it to an empty object.
   */
  resetSelectedConfiguration: () => void;
};

export function CreateNylasSchedulerConfigStore(defaultState: Partial<NylasSchedulerConfigStoreState> = {}): CreateNylasSchedulerConfigStoreReturnType {
  const defaultSchedulerConfigState: NylasSchedulerConfigStoreState = {
    selectedConfiguration: {},
    configurations: [],
    groupEvents: [],
    listConfigurationsNextCursor: null,
    calendars: [],
    currentUser: null,
    action: null,
    additionalParticipants: [],
    requiresSlug: false,
    masterGroupEvents: {},
    isLoading: false,
    ...defaultState,
  };
  const store = createStore<NylasSchedulerConfigStoreState>(defaultSchedulerConfigState);

  /**
   * Reset the selected configuration to the default state.
   * This is used to reset the selected configuration to the default state instead of setting it to an empty object.
   */
  function resetSelectedConfiguration() {
    store.set('selectedConfiguration', { ...defaultState['selectedConfiguration'] });
  }

  store.onChange('action', async action => {
    if (!action) {
      resetSelectedConfiguration();
    }
  });

  store.onChange('selectedConfiguration', async config => {
    const participantFetch = store.state.participantFetch;
    const participants = (config as RecursivePartial<Configuration>)?.participants;
    if (!participantFetch || !participants?.length) return;

    const emails = participants.filter((p): p is { email: string; is_organizer?: boolean } => !p.is_organizer && !!p.email).map(p => p.email);

    if (emails.length === 0) return;

    try {
      const grantId = store.state.participantFetchGrantId;
      const fetched = await participantFetch({ emails, grantId });
      if (!fetched?.length) return;

      const existing = store.state.additionalParticipants || [];
      const existingEmails = new Set(existing.map(p => p.email));
      const newParticipants = fetched.filter(p => !existingEmails.has(p.email));
      if (newParticipants.length > 0) {
        store.set('additionalParticipants', [...existing, ...newParticipants]);
      }
    } catch (err) {
      debug('[scheduler-config-store] participantFetch failed', err);
    }
  });

  return {
    ...store,
    resetSelectedConfiguration,
  };
}
